import { Fragment } from 'react';
import Select from "react-select";
import * as Yup from 'yup';
import { Formik } from 'formik';
import axios from "axios";
import API_ROUTES from "../../../config/api";

const companyTypes = [
  { value: 'ltd-company', label: 'LTD Company', color: '#00B8D9' },
  { value: 'sole-trader', label: 'Sole Trader', color: '#0052CC' },
  { value: 'partnership', label: 'Partnership', color: '#5243AA' },
  { value: 'llp', label: 'Limited Liability Company (LLP)', color: '#FF5630' },
  { value: 'plc', label: 'Public Listed Company (PLC)', color: '#FF8B00' },
];

const Organisation = ({ state, updateState, nextStep, addToStore }) => {
  return (
    <Formik
      initialValues={{
        organisation_name: state?.data?.company_details?.organisation_name || '',
        company_type: state?.data?.company_details?.company_type || '',
        website_url: state?.data?.company_details?.website_url || '',
        terms: state?.data?.company_details?.terms || false,
      }}
      validationSchema={Yup.object().shape({
        organisation_name: Yup.string()
          .min(2, 'Organisation name is too short')
          .required('Organisation name is required'),
        company_type: Yup.string()
          .required('Company type is required'),
        website_url: Yup.string()
          .url('Website URL must be a valid URL')
          .required('Website URL is required'),
        terms: Yup.bool()
          .oneOf([true], 'You must accept terms and conditions to continue'),
      })}
      onSubmit={async (values, {
        setSubmitting
      }) => {
        setSubmitting(true);

        if (values.company_type === 'ltd-company') {
          try {
            const companyResponse = await axios.post(API_ROUTES.onboarding['company:lookup'](), {
              company_name: values.organisation_name,
            });

            if (companyResponse?.data?.data?.company_status) {
              addToStore('companies_house', companyResponse?.data?.data);

              const vatResponse = await axios.post(API_ROUTES.user['vat:lookup'](), {
                company_number: companyResponse?.data?.data?.company_number,
              });

              if (vatResponse?.data?.data?.vat_number) {
                addToStore('vat_lookup', vatResponse?.data?.data?.vat_number);
              }
            }
          } catch (e) {
            console.log('lookup failed, moving on');
          }
        }

        updateState('company_details', values);
        nextStep();
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
        setFieldValue,
      }) => (
        <Fragment>

          <form action="#" method="POST" className="mt-8" onSubmit={handleSubmit}>

            <h1 className="text-2xl font-semibold mb-6">Your Details</h1>

            <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
              <div className="sm:col-span-2 mb-2">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-state">
                  Your Company Name *
                </label>
                <div className="mt-2.5">
                  <input type="text"
                    name="organisation_name"
                    id="organisation_name"
                    value={values.organisation_name}
                    autoComplete="organization"
                    placeholder={'e.g. Felloh LTD'}
                    disabled={isSubmitting}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    className={`block appearance-none w-full rounded-md border border-gray-300 text-gray-900 py-3 px-4 pr-8 rounded-lg leading-tight focus:outline-none focus:bg-white focus:border-gray-500${touched.organisation_name && errors.organisation_name ? ' border-red-500' : ''}`}
                  />
                </div>
                {touched.organisation_name && errors.organisation_name && <div className="field-error">{errors.organisation_name}</div>}
              </div>

              <div className="sm:col-span-2 mb-2">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-state">
                  Company Type *
                </label>
                <div className="mt-2.5">
                  <Select
                    defaultValue={[]}
                    name="company_type"
                    id="company_type"
                    options={companyTypes}
                    disabled={isSubmitting}
                    value={values.company_type ? companyTypes.find(option => option.value === values.company_type) : ''}
                    onChange={(option) => setFieldValue('company_type', option.value)}
                    onBlur={() => handleBlur('company_type')}
                    className={`block appearance-none w-full rounded-md border border-gray-300 text-gray-900 py-1 px-2 pr-2 rounded-lg leading-tight focus:outline-none focus:bg-white focus:border-gray-500${touched.company_type && errors.company_type ? ' border-red-500' : ''}`}
                    classNamePrefix="select"
                  />
                </div>
                {touched.company_type && errors.company_type && <div className="field-error">{errors.company_type}</div>}
              </div>

              <div className="sm:col-span-2">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-state">
                  Your Website URL *
                </label>
                <div className="mt-2.5">
                  <input type="text"
                    name="website_url"
                    id="website_url"
                    value={values.website_url}
                    onBlur={handleBlur}
                    disabled={isSubmitting}
                    onChange={handleChange}
                    placeholder={'e.g. https://felloh.com'}
                    className={`block appearance-none w-full rounded-md border border-gray-300 text-gray-900 py-3 px-4 pr-8 rounded-lg leading-tight focus:outline-none focus:bg-white focus:border-gray-500${touched.website_url && errors.website_url ? ' border-red-500' : ''}`}
                  />
                </div>
                {touched.website_url && errors.website_url && <div className="field-error">{errors.website_url}</div>}
              </div>

              <div className="flex gap-x-4 sm:col-span-2">
                <div className="flex items-center">
                  <input id="link-checkbox"
                    type="checkbox"
                    checked={values.terms}
                    onChange={() => setFieldValue('terms', !values.terms)}
                    className={`w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600${touched.terms && errors.terms ? ' border-red-500' : ''}`}
                  />
                  <div className="ml-2 text-sm">
                    <label htmlFor="link-checkbox"
                      className="text-sm font-medium">I agree with the felloh <a
                        href="https://www.felloh.com/privacy-policy" target="_blank" rel="noreferrer">terms and conditions</a>.
                    </label>
                    {touched.terms && errors.terms && (
                      <p id="helper-checkbox-text" className="field-error">{errors.terms}</p>
                    )}
                  </div>
                </div>
              </div>

            </div>

            <div className="mt-12">
              <button type="submit"
                disabled={isSubmitting || Object.keys(errors).length > 0 || Object.keys(touched).length === 0}
                className="bg-purple-800 disabled:bg-purple-200 hover:bg-purple-900 text-white font-semibold rounded-md py-2 px-4 w-full">
                {isSubmitting && (
                  <Fragment>
                    <svg aria-hidden="true" role="status" className="inline w-4 h-4 mr-3 text-white animate-spin"
                      viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="#E5E7EB" />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentColor" />
                    </svg>
                  </Fragment>
                )}
                {!isSubmitting && (
                  <Fragment>
                    Next
                  </Fragment>
                )}
              </button>
            </div>
          </form>
        </Fragment>
      )}
    </Formik>
  );
}

export default Organisation;
